import { activitySummary, type ActivityRecord } from "../shared/reminders.ts";
import { api } from "./api.ts";

const ACTIVITY_KEY = "neckmoves:activity-history";
const RETENTION_MS = 180 * 24 * 60 * 60 * 1_000;

export type ActiveActivity = { id: string; startedAt: number };

export function beginActivity(): ActiveActivity {
  return { id: crypto.randomUUID(), startedAt: Date.now() };
}

export function readActivityRecords(): ActivityRecord[] {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(ACTIVITY_KEY) || "[]");
    return Array.isArray(parsed) ? parsed.filter((item): item is ActivityRecord => (
      item && typeof item.id === "string" && typeof item.startedAt === "string"
        && typeof item.durationSeconds === "number" && ["completed", "stopped"].includes(item.outcome)
    )) : [];
  } catch {
    return [];
  }
}

export function finishActivity(active: ActiveActivity, outcome: ActivityRecord["outcome"]) {
  const now = Date.now();
  const record: ActivityRecord = {
    id: active.id,
    startedAt: new Date(active.startedAt).toISOString(),
    durationSeconds: Math.max(1, Math.round((now - active.startedAt) / 1_000)),
    outcome,
  };
  const retained = readActivityRecords().filter((item) => Date.parse(item.startedAt) >= now - RETENTION_MS && item.id !== record.id);
  window.localStorage.setItem(ACTIVITY_KEY, JSON.stringify([...retained, record].slice(-500)));
  window.dispatchEvent(new Event("neckmoves:activity-updated"));
  return record;
}

export function currentActivitySummary() {
  return activitySummary(readActivityRecords());
}

export async function syncAccountActivity() {
  const local = readActivityRecords();
  const result = await api<{ records: ActivityRecord[] }>("/api/activity/sync", {
    method: "POST",
    body: JSON.stringify({ records: local }),
  });
  const merged = new Map(result.records.map((record) => [record.id, record]));
  for (const record of readActivityRecords()) merged.set(record.id, record);
  const records = [...merged.values()].sort((a, b) => Date.parse(a.startedAt) - Date.parse(b.startedAt)).slice(-500);
  window.localStorage.setItem(ACTIVITY_KEY, JSON.stringify(records));
  window.dispatchEvent(new Event("neckmoves:activity-updated"));
  return records;
}
